import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Home, Info, Map, Briefcase, Shield, Book, Users, Heart, FileText, Download, Moon } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Sitemap = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const sitemapData = [
    {
      title: 'Main Pages',
      icon: Home,
      links: [
        { name: 'Home', path: '/' },
        { name: 'About Us', path: '/about-us' },
        { name: 'Uganda', path: '/uganda' },
        { name: 'Blog', path: '/blog' },
        { name: 'Contact', path: '/contact' }
      ]
    },
    {
      title: 'Experiences',
      icon: Map,
      links: [
        { name: 'All Experiences', path: '/all-experiences' },
        { name: 'Uganda City Tours', path: '/uganda-city-tours' },
        { name: 'Safari Packages', path: '/safari-packages' },
        { name: 'Cultural Tours', path: '/cultural-tours' },
        { name: 'Self-Drive Safaris', path: '/self-drive-safaris' },
        { name: 'Gorilla Trekking', path: '/gorilla-trekking' },
        { name: 'Chimpanzee Tracking', path: '/chimpanzee-tracking' },
        { name: 'Uganda Nightlife Guide', path: '/Uganda_Nightlife_Ultimate_Guide' }
      ]
    },
    {
      title: 'Guides',
      icon: Users,
      links: [
        { name: 'Find a Guide', path: '/travel-guides' },
        { name: 'Become a Guide', path: '/guide-application' },
        { name: 'Guide Profiles', path: '/travel-guides' }
      ]
    },
    {
      title: 'Blog Categories',
      icon: FileText,
      links: [
        { name: 'Travel Tips', path: '/blog?category=travel-tips' },
        { name: 'Safari Stories', path: '/blog?category=safari-stories' },
        { name: 'City Guides', path: '/blog?category=city-guides' },
        { name: 'Cultural Insights', path: '/blog?category=cultural-insights' }
      ]
    },
    {
      title: 'Resources',
      icon: Download,
      links: [
        { name: 'Free Travel Resources', path: '/resources' },
        { name: 'Gorilla Trekking Guide', path: '/resources#gorilla-guide' },
        { name: 'Safari Packing Checklist', path: '/resources#safari-checklist' },
        { name: 'Self-Drive Handbook', path: '/resources#self-drive' },
        { name: 'Uganda Maps', path: '/resources#uganda-map' }
      ]
    },
    {
      title: 'Careers & Partnership',
      icon: Briefcase,
      links: [
        { name: 'Careers', path: '/careers' },
        { name: 'Partnership', path: '/partnership' },
        { name: 'Write for Us', path: '/write-for-us' }
      ]
    },
    {
      title: 'Legal & Policies',
      icon: Shield,
      links: [
        { name: 'Privacy Policy', path: '/privacy-policy' },
        { name: 'Terms of Service', path: '/terms-of-service' },
        { name: 'Cookie Policy', path: '/cookie-policy' }
      ]
    },
    {
      title: 'Nightlife',
      icon: Moon,
      links: [
        { name: 'Uganda Nightlife Guide', path: '/Uganda_Nightlife_Ultimate_Guide' },
        { name: 'Night Crawling Packages', path: '/Uganda_Nightlife_Ultimate_Guide#packages' },
        { name: 'Club Guide', path: '/Uganda_Nightlife_Ultimate_Guide#clubs' },
        { name: 'Street Food Circuit', path: '/Uganda_Nightlife_Ultimate_Guide#food' }
      ]
    }
  ];

  const filteredSections = sitemapData.map(section => ({
    ...section,
    links: section.links.filter(link => 
      link.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(section => section.links.length > 0);

  return (
    <>
      <Navbar />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
            <div className="mb-4">
              <Link to="/" className="inline-flex items-center text-ta-green hover:text-ta-green-dark">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Home
              </Link>
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Sitemap</h1>
            <p className="text-gray-600 mb-6 max-w-3xl">
              Find everything you need on TravelCord. Browse our comprehensive sitemap to discover all available pages and resources.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-md relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search pages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 block w-full border border-gray-300 rounded-lg focus:ring-ta-green focus:border-ta-green"
              />
            </div>
          </div>
        </section>

        {/* Sitemap Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredSections.map((section, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center">
                      <section.icon className="h-5 w-5 text-ta-green mr-2" />
                      <h2 className="text-lg font-semibold text-gray-900">{section.title}</h2>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <ul className="space-y-2">
                      {section.links.map((link, linkIndex) => (
                        <li key={linkIndex}>
                          <Link 
                            to={link.path}
                            className="text-gray-600 hover:text-ta-green hover:underline flex items-center"
                          >
                            <span className="mr-2">•</span>
                            {link.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
      
      <Footer />
    </>
  );
};

export default Sitemap;